import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
	Alert,
	Button,
	Card,
	CardBody,
	CardHeader,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import {
	getWritingDeployApi,
	isDeployAwaitingReview,
	isDeployJobFailed,
	isDeployTerminal,
} from "../../../services/writingDeployApi";

const DEPLOYMENTS_PATH = "/aivisibility/deployments";

function safeReturnTo(raw) {
	const value = String(raw || "").trim();
	if (!value.startsWith("/aivisibility/")) return null;
	return value;
}

function formatStatus(status) {
	const s = String(status || "unknown").toLowerCase();
	return s.replace(/_/g, " ").toUpperCase();
}

function formatStartedLabel(dateString) {
	if (!dateString) return null;
	try {
		const d = new Date(dateString);
		if (Number.isNaN(d.getTime())) return null;
		return format(d, "d MMM yyyy · h:mm a");
	} catch {
		return null;
	}
}

function formatChangeLabel(laterJob) {
	const indices = Array.isArray(laterJob?.changeIndices)
		? laterJob.changeIndices
		: null;
	if (indices && indices.length > 0) {
		const nums = indices
			.map((i) => Number(i))
			.filter((n) => Number.isFinite(n))
			.map((n) => n + 1);
		if (nums.length === 1) return `change ${nums[0]}`;
		if (nums.length > 1) return `changes ${nums.join(", ")}`;
	}
	if (laterJob?.changeIndex != null && Number.isFinite(Number(laterJob.changeIndex))) {
		return `change ${Number(laterJob.changeIndex) + 1}`;
	}
	return null;
}

function formatLaterJobMeta(laterJob) {
	const change = formatChangeLabel(laterJob);
	const when = formatStartedLabel(laterJob?.overwrittenAt);
	const parts = [];
	if (change) parts.push(change);
	if (when) parts.push(`deployed ${when}`);
	return parts.join(" · ");
}

function deployDetailPath(jobId) {
	return `/aivisibility/deployments/view?jobId=${encodeURIComponent(jobId)}`;
}

function deployStatusBadgeClass(status, { isFailed = false } = {}) {
	const base = "writing-preview__status-badge";
	if (isFailed) {
		return `${base} writing-preview__status-badge--failed`;
	}
	const normalized = String(status || "").toLowerCase();
	if (normalized === "failed" || normalized === "error") {
		return `${base} writing-preview__status-badge--failed`;
	}
	if (normalized === "overwritten") {
		return `${base} writing-preview__status-badge--deployed`;
	}
	if (normalized === "discarded") {
		return `${base} writing-preview__status-badge--discarded`;
	}
	if (normalized === "reverted") {
		return `${base} writing-preview__status-badge--reverted`;
	}
	if (normalized === "awaiting_review") {
		return `${base} writing-preview__status-badge--ready`;
	}
	return base;
}

function LaterJobListItem({ laterJob }) {
	const jobId = laterJob?.jobId;
	const meta = formatLaterJobMeta(laterJob);
	return (
		<li>
			{jobId != null && jobId !== "" ? (
				<Link
					to={deployDetailPath(jobId)}
					className="writing-deploy__revert-job-link"
				>
					Job #{jobId}
				</Link>
			) : (
				<span>Job #?</span>
			)}
			{meta ? ` · ${meta}` : null}
		</li>
	);
}

function isDeploySettled(job) {
	const status = job?.status;
	return (
		isDeployAwaitingReview(status) ||
		isDeployTerminal(status) ||
		isDeployJobFailed(status, job?.errorMessage)
	);
}

function PagePane({ title, url, screenshotSrc, screenshotLoading }) {
	const [imgLoaded, setImgLoaded] = useState(false);

	useEffect(() => {
		setImgLoaded(false);
	}, [screenshotSrc]);

	const showRendering =
		screenshotLoading || (Boolean(screenshotSrc) && !imgLoaded);
	const showUnavailable = !screenshotLoading && !screenshotSrc;

	return (
		<Card className="shadow-sm writing-deploy__pane h-100">
			<CardHeader className="bg-white border-bottom py-2">
				<div className="writing-deploy__pane-heading">
					<h5 className="mb-0 writing-deploy__pane-title">{title}</h5>
					{url ? (
						<a
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							className="writing-deploy__pane-url writing-deploy__pane-visit"
							title={url}
						>
							Visit page
							<i className="fas fa-external-link-alt ms-1" aria-hidden />
						</a>
					) : null}
				</div>
			</CardHeader>
			<CardBody className="p-0">
				{screenshotSrc ? (
					<figure className="writing-deploy__shot mb-0">
						{showRendering ? (
							<div className="writing-deploy__iframe-empty text-muted small">
								<Spinner size="sm" className="me-2" />
								Rendering screenshot…
							</div>
						) : null}
						<img
							src={screenshotSrc}
							alt={`${title} screenshot`}
							onLoad={() => setImgLoaded(true)}
							onError={() => setImgLoaded(true)}
							style={imgLoaded ? undefined : { display: "none" }}
						/>
					</figure>
				) : showRendering ? (
					<div className="writing-deploy__iframe-empty text-muted small">
						<Spinner size="sm" className="me-2" />
						Rendering screenshot…
					</div>
				) : showUnavailable ? (
					<div className="writing-deploy__iframe-empty text-muted small">
						Screenshot unavailable
					</div>
				) : null}
			</CardBody>
		</Card>
	);
}

const AiVisibilityWritingDeployView = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const jobId = searchParams.get("jobId");
	const returnTo = safeReturnTo(searchParams.get("returnTo"));
	const startRevertRequested = searchParams.get("startRevert") === "1";
	const { currentWebsiteId, websitePlatform, userLoader, websiteLoader } =
		useContext(ProductContext);
	const deployApi = useMemo(
		() => getWritingDeployApi(websitePlatform),
		[websitePlatform]
	);

	const [phase, setPhase] = useState("idle");
	const [job, setJob] = useState(null);
	const [verification, setVerification] = useState(null);
	const [verificationLoading, setVerificationLoading] = useState(false);
	const [error, setError] = useState(null);
	const [actionLoading, setActionLoading] = useState(null);
	const [revertModalOpen, setRevertModalOpen] = useState(false);
	const [revertImpact, setRevertImpact] = useState(null);
	const [revertImpactLoading, setRevertImpactLoading] = useState(false);

	const goBack = useCallback(() => {
		// Only return to writing preview when we arrived with an explicit returnTo
		// (e.g. Deployed chip). List → deploy detail should go back to the list.
		if (returnTo) {
			navigate(returnTo);
			return;
		}
		navigate(DEPLOYMENTS_PATH);
	}, [navigate, returnTo]);

	const goDeployments = useCallback(() => {
		navigate(DEPLOYMENTS_PATH);
	}, [navigate]);

	const loadVerification = useCallback(async (targetJobId, settledJob) => {
		if (
			!isDeployAwaitingReview(settledJob?.status) &&
			!isDeployTerminal(settledJob?.status)
		) {
			setVerificationLoading(false);
			return;
		}
		setVerificationLoading(true);
		try {
			const ver = await deployApi.getDeployVerification(currentWebsiteId, targetJobId);
			if (ver.ok) {
				setVerification(ver.verification);
			}
		} finally {
			setVerificationLoading(false);
		}
	}, [currentWebsiteId, deployApi]);

	const loadReview = useCallback(
		async (targetJobId) => {
			if (!targetJobId || !currentWebsiteId) return;

			setPhase("loading");
			setError(null);
			setVerification(null);
			setVerificationLoading(false);

			const first = await deployApi.getDeployJob(currentWebsiteId, targetJobId);
			if (!first.ok) {
				setJob(null);
				setError(first.message || "Failed to load deploy job");
				setPhase("error");
				return;
			}

			setJob(first.job);

			// Viewing an already-finished job should not show the running pipeline UI.
			if (isDeploySettled(first.job)) {
				setPhase("review");
				if (isDeployJobFailed(first.job?.status, first.job?.errorMessage)) {
					setError(first.job?.errorMessage || "Deploy job failed");
				}
				await loadVerification(targetJobId, first.job);
				return;
			}

			setPhase("polling");
			const poll = await deployApi.pollDeployJob(currentWebsiteId, targetJobId, {
				untilStatuses: [
					"awaiting_review",
					"discarded",
					"overwritten",
					"reverted",
				],
				onTick: (tickJob) => setJob(tickJob),
			});

			if (!poll.ok) {
				setJob(poll.job);
				setError(poll.message || "Deploy job failed");
				setPhase(poll.job ? "review" : "error");
				return;
			}

			setJob(poll.job);
			setPhase("review");
			await loadVerification(targetJobId, poll.job);
		},
		[currentWebsiteId, loadVerification, deployApi]
	);

	useEffect(() => {
		if (userLoader || websiteLoader) return;
		if (!jobId) {
			setPhase("idle");
			return;
		}
		if (!currentWebsiteId) return;
		loadReview(jobId);
	}, [jobId, currentWebsiteId, loadReview, userLoader, websiteLoader]);

	const closeRevertModal = useCallback(() => {
		if (actionLoading || revertImpactLoading) return;
		setRevertModalOpen(false);
		setRevertImpact(null);
	}, [actionLoading, revertImpactLoading]);

	const openRevertConfirm = useCallback(async () => {
		if (!currentWebsiteId || !jobId || actionLoading) return;
		setRevertImpactLoading(true);
		setRevertModalOpen(true);
		setRevertImpact(null);
		try {
			const impact = await deployApi.getRevertImpact(currentWebsiteId, jobId);
			if (!impact.ok) {
				toast.error(impact.message || "Failed to check revert impact");
				setRevertModalOpen(false);
				return;
			}
			if (impact.canRevert === false) {
				toast.error(impact.message || impact.warning || "This job cannot be reverted");
				setRevertModalOpen(false);
				return;
			}
			setRevertImpact(impact);
		} catch {
			toast.error("Failed to check revert impact");
			setRevertModalOpen(false);
		} finally {
			setRevertImpactLoading(false);
		}
	}, [actionLoading, currentWebsiteId, jobId, deployApi]);

	// From Writing Assistance sidebar Revert → open impact modal once job is ready
	useEffect(() => {
		if (!startRevertRequested) return;
		if (phase !== "review" || !job) return;
		const canRevert =
			String(job.status || "").toLowerCase() === "overwritten" && !job.isRevert;
		if (!canRevert) return;

		const params = new URLSearchParams(searchParams);
		params.delete("startRevert");
		navigate(`/aivisibility/deployments/view?${params.toString()}`, {
			replace: true,
		});
		openRevertConfirm();
	}, [
		startRevertRequested,
		phase,
		job,
		searchParams,
		navigate,
		openRevertConfirm,
	]);

	const confirmStartRevert = useCallback(async () => {
		if (!currentWebsiteId || !jobId || actionLoading) return;
		setRevertModalOpen(false);
		setActionLoading("revert");
		setPhase("action");
		try {
			const start = await deployApi.startRevertDeployJob(currentWebsiteId, jobId);
			if (!start.ok) {
				toast.error(start.message || "Failed to start revert preview");
				setPhase("review");
				return;
			}

			const poll = await deployApi.pollDeployJob(currentWebsiteId, jobId, {
				untilStatuses: ["awaiting_review"],
				onTick: (tickJob) => setJob(tickJob),
			});

			if (!poll.ok) {
				toast.error(poll.message || "Revert preview failed");
				setJob(poll.job);
				setError(poll.message || "Revert preview failed");
				setPhase(poll.job ? "review" : "error");
				return;
			}

			setJob(poll.job);
			setPhase("review");
			await loadVerification(jobId, poll.job);
		} catch {
			toast.error("Failed to start revert preview");
			setPhase("review");
		} finally {
			setActionLoading(null);
			setRevertImpact(null);
		}
	}, [actionLoading, currentWebsiteId, jobId, loadVerification, deployApi]);

	const runAction = useCallback(
		async (kind) => {
			if (!currentWebsiteId || !jobId || actionLoading) return;
			const isRevert = Boolean(job?.isRevert);
			setActionLoading(kind);
			setPhase("action");
			try {
				const start =
					kind === "discard"
						? await deployApi.discardDeployJob(currentWebsiteId, jobId)
						: await deployApi.overwriteDeployJob(currentWebsiteId, jobId);

				if (!start.ok) {
					toast.error(start.message || `Failed to ${kind}`);
					setPhase("review");
					return;
				}

				let until;
				if (isRevert) {
					until = kind === "discard" ? ["overwritten"] : ["reverted"];
				} else {
					until = kind === "discard" ? ["discarded"] : ["overwritten"];
				}

				const poll = await deployApi.pollDeployJob(currentWebsiteId, jobId, {
					untilStatuses: until,
					onTick: (tickJob) => setJob(tickJob),
				});

				if (!poll.ok) {
					toast.error(poll.message || `Failed to ${kind}`);
					setJob(poll.job);
					setError(poll.message || `Failed to ${kind}`);
					setPhase(poll.job ? "review" : "error");
					return;
				}

				setJob(poll.job);
				setPhase("review");
				if (isRevert) {
					toast.success(
						kind === "discard"
							? "Revert preview cancelled. Live page unchanged."
							: "Restored to pre-deploy backup"
					);
				} else {
					toast.success(
						kind === "discard" ? "Deploy discarded" : "Deployed to live site"
					);
				}
			} catch {
				toast.error(`Failed to ${kind}`);
				setPhase("review");
			} finally {
				setActionLoading(null);
			}
		},
		[actionLoading, currentWebsiteId, job, jobId, deployApi]
	);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!jobId) {
		return (
			<div className="contentBlock ai-visibility-dashboard">
				<p className="text-muted">No deploy job selected.</p>
				<Button color="primary" onClick={goDeployments}>
					Back to Deployments
				</Button>
			</div>
		);
	}

	if (!currentWebsiteId && phase !== "error") {
		return <DetailSkeleton />;
	}

	const statusLabel =
		job?.currentStep ||
		(job?.status ? formatStatus(job.status) : null);

	const actionBusyLabel = (() => {
		if (actionLoading === "revert") return "Starting revert preview";
		if (actionLoading === "discard") {
			return job?.isRevert ? "Cancelling revert" : "Discarding";
		}
		if (actionLoading === "overwrite") {
			return job?.isRevert ? "Confirming revert" : "Deploying to live";
		}
		return "Working";
	})();

	// During overwrite/discard/revert, job.status often stays awaiting_review until
	// the terminal state lands — prefer the action label so the badge matches the UI.
	const busyBadgeLabel =
		phase === "action"
			? formatStatus(actionBusyLabel)
			: statusLabel;

	if (phase === "polling" || phase === "action") {
		return (
			<div className="contentBlock ai-visibility-dashboard writing-deploy-page">
				<div className="writing-preview__header">
					<div className="writing-preview__header-left">
						<button
							type="button"
							className="writing-preview__back"
							onClick={goBack}
						>
							<i className="fas fa-arrow-left me-2" />
							Back
						</button>
						{job?.sourceUrl && (
							<a
								href={job.sourceUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="writing-preview__header-url"
								title={job.sourceUrl}
							>
								{job.sourceUrl}
							</a>
						)}
					</div>
					{busyBadgeLabel && (
						<span className="writing-preview__status-badge writing-preview__status-badge--processing">
							{busyBadgeLabel}
						</span>
					)}
				</div>
				<div className="writing-preview__processing">
					<Spinner color="primary" className="mb-3" />
					<p className="writing-preview__processing-title mb-1">
						{phase === "action"
							? `${actionBusyLabel}…`
							: `${statusLabel || "Running deployment pipeline"}…`}
					</p>
					<p className="writing-preview__processing-subtitle text-muted mb-0">
						This may take a minute while we prepare the preview.
					</p>
				</div>
			</div>
		);
	}

	if (phase === "error") {
		return (
			<div className="contentBlock ai-visibility-dashboard writing-deploy-page">
				<Alert color="danger">{error || "Deploy job failed."}</Alert>
				{job?.errorMessage && (
					<p className="text-muted small">{job.errorMessage}</p>
				)}
				<div className="d-flex gap-2">
					<Button color="primary" onClick={goBack}>
						Back
					</Button>
					{job?.writingJobId != null && (
						<Button
							color="light"
							className="border"
							onClick={() =>
								navigate(
									`/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(
										job.writingJobId
									)}`
								)
							}
						>
							Back to writing preview
						</Button>
					)}
				</div>
			</div>
		);
	}

	if (phase === "review" && job) {
		const diff = verification?.diffSummary || {};
		const startedLabel = formatStartedLabel(job.startedAt || job.createdAt);
		const similarityPct =
			diff.similarity != null
				? `${(Number(diff.similarity) * 100).toFixed(2)}%`
				: null;
		const canAct = isDeployAwaitingReview(job.status);
		const isRevertReview = canAct && Boolean(job.isRevert);
		const canRevert =
			String(job.status).toLowerCase() === "overwritten" && !job.isRevert;
		const isTerminal = isDeployTerminal(job.status);
		const isFailed = isDeployJobFailed(job.status, job.errorMessage);
		const isDone = isTerminal || isFailed;
		const laterJobs = revertImpact?.laterJobs || [];
		const hasLaterJobs = laterJobs.length > 0;

		const jobCardTitle = isFailed
			? "Failed Job"
			: job.status === "overwritten"
				? "Deploy Job"
				: job.status === "discarded"
					? "Discard Job"
					: job.status === "reverted"
						? "Reverted Job"
						: isRevertReview
							? "Revert Preview"
							: "Job";

		let terminalCopy = null;
		if (job.status === "overwritten") {
			terminalCopy =
				"This deploy was applied to the live page. You can start a revert preview to restore the pre-deploy backup.";
		} else if (job.status === "discarded") {
			terminalCopy =
				"This deploy was discarded. The preview was not applied to the live page.";
		} else if (job.status === "reverted") {
			terminalCopy = "Restored to pre-deploy backup.";
		} else if (isTerminal) {
			terminalCopy = `Job finished with status ${formatStatus(job.status)}.`;
		}

		const subtitle = (() => {
			if (isRevertReview) {
				return "Review the backup preview before restoring it to the live page.";
			}
			if (canAct) {
				return "Review the preview before overwriting the live page.";
			}
			if (isFailed) return "This deployment failed and was not applied.";
			if (job.status === "reverted") {
				return "This job was successfully reverted to the pre-deploy backup.";
			}
			return "Review the deployment result for this job.";
		})();

		return (
			<div className="contentBlock ai-visibility-dashboard writing-deploy-page writing-assistance">
				<div className="writing-preview__header mb-3">
					<div className="writing-preview__header-left">
						<button
							type="button"
							className="writing-preview__back"
							onClick={goBack}
						>
							<i className="fas fa-arrow-left me-2" />
							Back
						</button>
					</div>
					<span
						className={deployStatusBadgeClass(job.status, { isFailed })}
					>
						{formatStatus(job.status)}
						{isRevertReview ? " · revert preview" : ""}
					</span>
				</div>

				<div className="pageHeader mb-3 d-flex flex-wrap align-items-start justify-content-between gap-2">
					<div>
						<h2 className="pageTitle mb-1">Deployment Progress</h2>
						<p className="text-muted mb-0">{subtitle}</p>
					</div>
					{isDone && job.writingJobId != null && (
						<Button
							color="light"
							className="border"
							onClick={() =>
								navigate(
									`/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(
										job.writingJobId
									)}`
								)
							}
						>
							Back to writing preview
						</Button>
					)}
				</div>

				<Card className="shadow-sm mb-3">
					<CardHeader className="bg-white border-bottom">
						<h5 className="mb-0">{jobCardTitle}</h5>
					</CardHeader>
					<CardBody className="writing-deploy__job-meta">
						{isTerminal && !isFailed && terminalCopy ? (
							<p className="mb-3">{terminalCopy}</p>
						) : null}
						{startedLabel && (
							<p className="mb-2">
								<span className="text-muted">Started:</span> {startedLabel}
							</p>
						)}
						{job.sourceUrl && (
							<p className="mb-2">
								<span className="text-muted">Source URL:</span>{" "}
								<a
									href={job.sourceUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="writing-deploy__preview-link"
								>
									{job.sourceUrl}
									<i className="fas fa-external-link-alt ms-1 small" />
								</a>
							</p>
						)}
						{job.tempPageUrl && canAct && (
							<p className="mb-2">
								<span className="text-muted">Preview page:</span>{" "}
								<a
									href={job.tempPageUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="writing-deploy__preview-link"
								>
									{job.tempPageUrl}
									<i className="fas fa-external-link-alt ms-1 small" />
								</a>
							</p>
						)}
						{isFailed && job.instructions ? (
							<div className="writing-deploy__instructions mt-3">
								<p className="text-muted mb-1">Attempted change:</p>
								<pre className="writing-deploy__instructions-pre mb-0">
									{job.instructions}
								</pre>
							</div>
						) : null}
					</CardBody>
				</Card>

				{verification && (
					<Card className="shadow-sm mb-3">
						<CardHeader className="bg-white border-bottom">
							<h5 className="mb-0">
								{isRevertReview
									? "Revert Verification Report"
									: "Verification Report"}
							</h5>
						</CardHeader>
						<CardBody>
							<div className="writing-deploy__verification-summary">
								{verification.noindexVerified != null && (
									<p className="mb-2">
										<span className="text-muted">NoIndex verified:</span>{" "}
										{verification.noindexVerified ? "Yes" : "No"}
									</p>
								)}
								{similarityPct && (
									<p className="mb-0">
										<span className="text-muted">Visual similarity:</span>{" "}
										{similarityPct}
									</p>
								)}
							</div>
						</CardBody>
					</Card>
				)}

				{!isFailed && (
					<div className="writing-deploy__compare mb-4">
						<PagePane
							title={isRevertReview ? "Current Live Page" : "Original Page"}
							url={job.sourceUrl}
							screenshotSrc={verification?.screenshotOriginal}
							screenshotLoading={verificationLoading}
						/>
						<PagePane
							title={
								isRevertReview
									? "Backup Preview (to restore)"
									: "Modified Page"
							}
							url={canAct ? job.tempPageUrl : null}
							screenshotSrc={verification?.screenshotModified}
							screenshotLoading={verificationLoading}
						/>
					</div>
				)}

				{canAct && (
					<div className="writing-deploy__actions">
						{isRevertReview ? (
							<>
								<Button
									color="success"
									disabled={!!actionLoading}
									onClick={() => runAction("overwrite")}
								>
									{actionLoading === "overwrite" ? (
										<>
											<Spinner size="sm" className="me-1" />
											Confirming…
										</>
									) : (
										"Confirm Revert"
									)}
								</Button>
								<Button
									color="danger"
									outline
									disabled={!!actionLoading}
									onClick={() => runAction("discard")}
								>
									{actionLoading === "discard" ? (
										<>
											<Spinner size="sm" className="me-1" />
											Cancelling…
										</>
									) : (
										"Cancel Revert"
									)}
								</Button>
							</>
						) : (
							<>
								<Button
									color="success"
									disabled={!!actionLoading}
									onClick={() => runAction("overwrite")}
								>
									{actionLoading === "overwrite" ? (
										<>
											<Spinner size="sm" className="me-1" />
											Deploying…
										</>
									) : (
										"Overwrite Original URL"
									)}
								</Button>
								<Button
									color="danger"
									outline
									disabled={!!actionLoading}
									onClick={() => runAction("discard")}
								>
									{actionLoading === "discard" ? (
										<>
											<Spinner size="sm" className="me-1" />
											Discarding…
										</>
									) : (
										"Discard"
									)}
								</Button>
							</>
						)}
					</div>
				)}

				{canRevert && (
					<div className="writing-deploy__actions">
						<Button
							color="warning"
							disabled={!!actionLoading || revertImpactLoading}
							onClick={openRevertConfirm}
						>
							{revertImpactLoading ? (
								<>
									<Spinner size="sm" className="me-1" />
									Checking…
								</>
							) : (
								"Revert to original"
							)}
						</Button>
					</div>
				)}

				<Modal
					isOpen={revertModalOpen}
					toggle={closeRevertModal}
					centered
					size="md"
					className="writing-preview__deploy-confirm-modal writing-deploy__revert-modal"
				>
					<ModalHeader
						toggle={
							actionLoading || revertImpactLoading ? undefined : closeRevertModal
						}
					>
						{hasLaterJobs ? "Confirm revert" : "Start revert preview?"}
					</ModalHeader>
					<ModalBody>
						{revertImpactLoading || !revertImpact ? (
							<div className="d-flex align-items-center gap-2 text-muted">
								<Spinner size="sm" />
								Checking later deployments…
							</div>
						) : hasLaterJobs ? (
							<div className="writing-deploy__revert-impact">
								<Alert color="warning" className="writing-deploy__revert-alert mb-3">
									Later successful deployments on this page will no longer be
									visible after this revert. Restoring this backup overwrites
									the current live content.
								</Alert>
								<p className="writing-deploy__revert-list-label mb-2">
									Later deployments that will no longer be visible
								</p>
								<ul className="writing-deploy__revert-job-list mb-3">
									{laterJobs.map((lj) => (
										<LaterJobListItem
											key={lj.jobId ?? formatLaterJobMeta(lj)}
											laterJob={lj}
										/>
									))}
								</ul>
								<p className="mb-0 text-muted small">
									You’ll review screenshots before the live page is restored.
								</p>
							</div>
						) : (
							<p className="mb-0">
								Start a revert preview? You’ll review screenshots before the
								live page is restored to the pre-deploy backup.
							</p>
						)}
					</ModalBody>
					<ModalFooter>
						<Button
							color="light"
							className="border"
							disabled={!!actionLoading || revertImpactLoading}
							onClick={closeRevertModal}
						>
							Cancel
						</Button>
						<Button
							color="primary"
							disabled={!!actionLoading || revertImpactLoading || !revertImpact}
							onClick={confirmStartRevert}
						>
							{hasLaterJobs ? "Continue" : "Start revert preview"}
						</Button>
					</ModalFooter>
				</Modal>
			</div>
		);
	}

	return <DetailSkeleton />;
};

export default AiVisibilityWritingDeployView;
